import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState } from "react";
import {
  XMarkIcon,
  EyeIcon,
  PencilIcon,
  UserPlusIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import ViewPrecinctAssignmentModal from "./ViewPrecinctAssignmentModal";
import EditPrecinctDetails from "./EditPrecinctDetails";
import AssignVoterModal from "./AssignVoterModal";
import Table from "../../common/Table";
import { votersAPI } from "../../../services/api";
import AddNewPrecinct from "./AddNewPrecinct";
import { useMutation, useQuery, useQueryClient } from "react-query";

function PrecinctManagementModal({ isOpen, onClose }) {
  const [search, setSearch] = useState("");
  const [precincts, setPrecincts] = useState([]);
  const [addingPrecinct, setAddingPrecinct] = useState(false);

  // Modal states
  const [selectedPrecinct, setSelectedPrecinct] = useState(null);
  const [editingPrecinct, setEditingPrecinct] = useState(null);
  const [assigningPrecinct, setAssigningPrecinct] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const queryClient = useQueryClient();

  // Mutations
  const createPrecinctMutation = useMutation(
    (payload) => votersAPI.createPrecinct(payload),
    {
      onSuccess: async () => {
        setAddingPrecinct(false);
        queryClient.invalidateQueries("precincts");
      },
    }
  );

  const updatePrecinctMutation = useMutation(
    ({ id, data }) => votersAPI.updatePrecinct(id, data),
    {
      onSuccess: async () => {
        queryClient.invalidateQueries("precincts");
        setEditingPrecinct(null);
      },
    }
  );

  const precinctsQuery = useQuery(
    ["precincts", { page: currentPage, per_page: itemsPerPage, q: search }],
    async () => {
      const hasQuery = !!(search && search.trim().length > 0);
      if (hasQuery) {
        const res = await votersAPI.searchPrecinct(search.trim());
        const d = res?.data?.data ?? res?.data ?? {};
        let precinctsData = [];
        if (Array.isArray(d?.precincts)) {
          precinctsData = d.precincts;
        } else if (Array.isArray(d?.searched_precinct)) {
          precinctsData = d.searched_precinct;
        } else if (Array.isArray(d?.results)) {
          precinctsData = d.results;
        } else if (Array.isArray(d)) {
          precinctsData = d;
        } else if (d?.precinct) {
          precinctsData = [d.precinct];
        }
        setPrecincts(precinctsData);
        setTotalItems(precinctsData.length || 0);
        setTotalPages(1);
        return { precincts: precinctsData, meta: { total: precinctsData.length || 0, last_page: 1 } };
      } else {
        const params = {
          page: currentPage,
          per_page: itemsPerPage,
        };
        const res = await votersAPI.getPrecincts(params);
        if (res.data?.success) {
          const precinctsData = res.data.data?.precincts || [];
          const meta = res.data.data?.meta || {};
          setPrecincts(precinctsData);
          setTotalItems(Number(meta.total) || precinctsData.length || 0);
          setTotalPages(Number(meta.last_page) || 1);
          return { precincts: precinctsData, meta };
        }
        throw new Error(res.data?.message || "Failed to load precincts.");
      }
    },
    { enabled: isOpen }
  );

  // Handle editing precinct
  const handleSaveEdit = async (updatedPrecinct) => {
    try {
      await updatePrecinctMutation.mutateAsync({ id: updatedPrecinct.precinct_id, data: updatedPrecinct });
    } catch (err) {
      console.error("Error updating precinct:", err);
    }
  };

  const columns = [
    {
      key: "name",
      label: "Precinct Name",
      render: (_, p) => <span>{p.name}</span>,
    },
    {
      key: "location",
      label: "Location",
      render: (_, p) => <span>{p.location}</span>,
    },
    {
      key: "total_assigned_voters",
      label: "Total Assigned Voters",
      render: (_, p) => <span>{p.total_assigned_voters ?? "0"}</span>,
    },
    {
      key: "actions",
      label: "Actions",
      render: (_, p) => (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setSelectedPrecinct(p)}
            className="p-2 rounded-full hover:bg-blue-100 active:bg-blue-200 transition"
          >
            <EyeIcon className="w-4 h-4 text-blue-600" />
          </button>

          <button
            onClick={() => setEditingPrecinct(p)}
            className="p-2 rounded-full hover:bg-yellow-100 active:bg-yellow-200 transition"
          >
            <PencilIcon className="w-4 h-4 text-yellow-500" />
          </button>

          <button
            onClick={() => setAssigningPrecinct(p)}
            className="p-2 rounded-full hover:bg-green-100 active:bg-green-200 transition"
          >
            <UserPlusIcon className="w-4 h-4 text-green-600" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        {/* Main content */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-6 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-300 -mx-6 px-6">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-bold text-gray-900"
                  >
                    Precinct Management
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="p-2 rounded-full hover:bg-gray-100 transition-transform transform hover:rotate-90 duration-200"
                  >
                    <XMarkIcon className="w-6 h-6 text-gray-500" />
                  </button>
                </div>

                {/* Add/Search */}
                <div className="flex justify-between items-center mb-4">
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium"
                    onClick={() => setAddingPrecinct(true)}>
                    + Add Precinct
                  </button>

                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search precinct..."
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="border rounded-md pl-10 pr-3 py-2 text-sm w-96"
                    />
                  </div>
                </div>

                {/* Scrollable Table */}
                <div className="overflow-y-auto max-h-80 border border-gray-300 rounded-t-lg">
                  {precinctsQuery.isLoading ? (
                    <div className="text-center py-6 text-gray-500">
                      Loading precincts...
                    </div>
                  ) : precinctsQuery.isError ? (
                    <div className="text-center py-6 text-red-500">Failed to load precincts.</div>
                  ) : (
                    <Table
                      noContainer={true}
                      columns={columns}
                      data={precincts}
                      getRowKey={(p) => p.precinct_id}
                      pagination={{
                        currentPage,
                        lastPage: totalPages,
                        perPage: itemsPerPage,
                        total: totalItems,
                      }}
                      onPageChange={(page, newPerPage = itemsPerPage) => {
                        setCurrentPage(page);
                        if (newPerPage !== itemsPerPage) {
                          setItemsPerPage(newPerPage);
                          setCurrentPage(1);
                        }
                      }}
                      paginationProps={{
                        onPerPageChange: (value) => {
                          setItemsPerPage(value);
                          setCurrentPage(1);
                        },
                        showSizeSelector: true,
                        isLoading: precinctsQuery.isLoading,
                      }}
                    />
                  )}
                </div>

                <AddNewPrecinct
                  isOpen={addingPrecinct}
                  onClose={() => setAddingPrecinct(false)}
                  onSubmit={async (data) => {
                    try {
                      await createPrecinctMutation.mutateAsync({ name: data.name, location: data.location });
                    } catch (err) {
                      console.error("Error creating precinct:", err);
                    }
                  }}
                />

                {/* Close */}
                <div className="border-t border-gray-300 mt-4 pt-4 -mx-6 px-6 flex justify-end">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm"
                  >
                    Close
                  </button>
                </div>

                {/* View Modal */}
                <ViewPrecinctAssignmentModal
                  isOpen={!!selectedPrecinct}
                  onClose={() => setSelectedPrecinct(null)}
                  precinct={selectedPrecinct}
                />

                {/* Edit Modal */}
                <EditPrecinctDetails
                  isOpen={!!editingPrecinct}
                  onClose={() => setEditingPrecinct(null)}
                  precinct={editingPrecinct}
                  onSave={handleSaveEdit}
                />

                {/* Assign Modal */}
                <AssignVoterModal
                  isOpen={!!assigningPrecinct}
                  onClose={() => setAssigningPrecinct(null)}
                  precinct={assigningPrecinct}
                  onAssigned={async () => {
                    try {
                      await queryClient.invalidateQueries("precincts");
                    } catch (e) {
                      console.error("Failed to refresh precincts after assignment", e);
                    }
                  }}
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default PrecinctManagementModal;
